import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  MinusCircleOutlined,
} from '@ant-design/icons';
import { Badge, Checkbox } from 'antd';
import { TablePaginationConfig } from 'antd/es/table';
import { ColumnProps } from 'antd/lib/table';
import { SorterResult } from 'antd/lib/table/interface';

import IBTooltip from '@totem/components/common/helpTooltips/IBTooltip';
import { EMPTY_ID } from '@totem/components/common/reference/ReferenceSelector';
import DeviceActionMenu from '@totem/components/devices/DeviceActionMenu';
import DeviceContext from '@totem/components/devices/devicesContainer/deviceContainerContext';
import { isManaged } from '@totem/components/devices/devicesContainer/utilities';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';
import InputContext from '@totem/components/ui_components/reportSettings/InputContext';
import { permissions } from '@totem/components/userContext/security/permissions/permissions';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import VendorDisplay from '@totem/components/vendors/vendorLogos/vendorDisplay';
import { DeviceQueryResult } from '@totem/types/devices';
import { isNotNull, isNull } from '@totem/utilities/common';
import { nullifyIfEmpty } from '@totem/utilities/tableFilterUtilities';
import { sortStringAscending } from '@totem/utilities/tableUtilities';

import '../devices.css';

type Props = {
  showMultiselect?: boolean;
};

const DevicesContainerTable = ({ showMultiselect }: Props) => {
  const { userProfile } = useContext(UserProfileContext);
  const {
    loading,
    deviceData,
    totalRecords,
    selectedDevices,
    selectDevice,
    deselectDevice,
    clearSelectedDevices,
  } = useContext(DeviceContext);
  const { input, handleTableChange, getFilterValues, getFilterOptions } =
    useContext(InputContext);

  const isSysAdmin =
    permissions.systemAdministrator.hasPermissions(userProfile);
  const canMultiSelect =
    permissions.teamMember.hasPermissions(userProfile) &&
    (isNotNull(showMultiselect) ? showMultiselect : false);

  const getBuildingDisplay = (device: DeviceQueryResult) => {
    if (
      device.building !== null &&
      device.building.id !== null &&
      device.building.id !== EMPTY_ID
    ) {
      return (
        <Link to={`/dashboard/buildings/${device.building.id}`}>
          {device.building.name}
        </Link>
      );
    } else if (device.building !== null) {
      return <span>{device.building.name}</span>;
    }
    return <span>-</span>;
  };

  const getControlSystemDisplay = (device: DeviceQueryResult) => {
    if (
      device.controlSystem !== null &&
      device.controlSystem.id !== null &&
      device.controlSystem.id !== EMPTY_ID
    ) {
      return (
        <Link to={`/dashboard/controlsystems/${device.controlSystem.id}`}>
          {device.controlSystem.name}
        </Link>
      );
    } else if (device.controlSystem !== null) {
      return <span>{device.controlSystem.name}</span>;
    }
    return <span>-</span>;
  };

  const getFirmwareDisplay = (device: DeviceQueryResult) => {
    return device.device.firmware;
  };

  const columns: ColumnProps<DeviceQueryResult>[] = [
    {
      title: 'Name',
      dataIndex: 'device.displayName',
      key: 'displayName',
      showSorterTooltip: true,
      render: (_, device: DeviceQueryResult) => (
        <Link to={`/dashboard/devices/${device.device.id}`}>
          {device.device.displayName}
        </Link>
      ),
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        sortStringAscending(compA.device.displayName, compB.device.displayName),
    },
    {
      title: 'Type',
      dataIndex: 'device.type',
      key: 'type',
      showSorterTooltip: true,
      render: (_, device: DeviceQueryResult) => device.device.type,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        sortStringAscending(compA.device.type, compB.device.type),
      filterMultiple: true,
      filteredValue: getFilterValues('type'),
      filters: getFilterOptions('type'),
    },
    {
      title: 'Vendor',
      dataIndex: 'device.vendor',
      key: 'vendor',
      showSorterTooltip: true,
      render: (_, device: DeviceQueryResult) => (
        <VendorDisplay vendorName={device.device.vendor} />
      ),
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        sortStringAscending(compA.device.vendor, compB.device.vendor),
      filterMultiple: true,
      filteredValue: getFilterValues('vendor'),
      filters: getFilterOptions('vendor'),
      filterDropdownProps: {
        placement: 'bottom',
      },
    },
    {
      title: 'OS',
      dataIndex: 'device.operatingSystem',
      key: 'operatingSystem',
      showSorterTooltip: true,
      render: (_, device: DeviceQueryResult) => device.device.operatingSystem,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        sortStringAscending(
          compA.device.operatingSystem,
          compB.device.operatingSystem,
        ),
      filterMultiple: true,
      filteredValue: getFilterValues('operatingSystem'),
      filters: getFilterOptions('operatingSystem'),
    },
    {
      title: 'Firmware',
      dataIndex: 'device.firmware',
      key: 'firmware',
      showSorterTooltip: true,
      render: (_, device: DeviceQueryResult) => getFirmwareDisplay(device),
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        sortStringAscending(compA.device.firmware, compB.device.firmware),
      filterMultiple: true,
      filteredValue: getFilterValues('firmware'),
      filters: getFilterOptions('firmware'),
    },
    {
      title: 'IP Addresses',
      dataIndex: 'device.ipAddresses',
      key: 'ipAddresses',
      showSorterTooltip: true,
      render: (_, device: DeviceQueryResult) => (
        <span>
          {device.device.ipAddresses !== null &&
            device.device.ipAddresses.map((addr, idx) =>
              idx === 0 ? (
                <span key={addr}>{addr}</span>
              ) : (
                <span key={addr}>
                  <br />
                  {addr}
                </span>
              ),
            )}
        </span>
      ),
    },
    {
      title: 'Building',
      dataIndex: 'building.name',
      key: 'building',
      showSorterTooltip: true,
      render: (_, device: DeviceQueryResult) => getBuildingDisplay(device),
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        sortStringAscending(compA.building.name, compB.building.name),
      filterMultiple: true,
      filteredValue: getFilterValues('building'),
      filters: getFilterOptions('building'),
    },
    {
      title: 'Control System',
      dataIndex: 'controlSystem.name',
      key: 'controlSystem',
      showSorterTooltip: true,
      render: (_, device: DeviceQueryResult) => getControlSystemDisplay(device),
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        sortStringAscending(compA.controlSystem.name, compB.controlSystem.name),
    },
    {
      title: 'Actions',
      width: 30,
      dataIndex: 'additionalOptions',
      key: 'additionalOptions',
      render: (_, device: DeviceQueryResult) => (
        <div className="center-table-cell">
          <DeviceActionMenu device={device} />
        </div>
      ),
    },
  ];

  const getColumns = () => {
    const cols = [...columns];

    if (isSysAdmin) {
      cols.splice(5, 0, {
        title: (
          <IBTooltip content={permissions.systemAdministrator.tooltip}>
            Managed
          </IBTooltip>
        ),
        dataIndex: 'device.flags.managed.status',
        key: 'isManaged',
        showSorterTooltip: true,
        render: (_, device: DeviceQueryResult) =>
          isManaged(device) ? (
            <CheckCircleOutlined
              style={{
                color: 'green',
                fontSize: '20px',
              }}
            />
          ) : (
            <MinusCircleOutlined
              style={{
                fontSize: '20px',
              }}
            />
          ),
        sortDirections: ['ascend', 'descend'],
        sorter: (compA, compB) =>
          sortStringAscending(
            compA.device.flags.managed.status,
            compB.device.flags.managed.status,
          ),
        filterMultiple: false,
        filteredValue: nullifyIfEmpty(getFilterValues('isManaged')),
        filters: [
          {
            text: (
              <span>
                <CheckCircleOutlined
                  style={{
                    color: 'green',
                    fontSize: '20px',
                    marginRight: '15px',
                  }}
                />
                &nbsp;Is Managed
              </span>
            ),
            value: 'ACTIVE',
          },
          {
            text: (
              <span>
                <MinusCircleOutlined
                  style={{
                    fontSize: '20px',
                    marginRight: '15px',
                  }}
                />
                &nbsp;Not Managed
              </span>
            ),
            value: 'INACTIVE',
          },
        ],
      });

      cols.splice(6, 0, {
        title: 'Monitored',
        dataIndex: 'device.flags.monitored.status',
        key: 'isMonitored',
        showSorterTooltip: true,
        render: (_, device: DeviceQueryResult) =>
          device.device.flags.monitored.status === 'ACTIVE' ? (
            <CheckCircleOutlined
              style={{
                color: 'green',
                fontSize: '20px',
              }}
            />
          ) : (
            <MinusCircleOutlined
              style={{
                fontSize: '20px',
              }}
            />
          ),
        sortDirections: ['ascend', 'descend'],
        sorter: (compA, compB) =>
          sortStringAscending(
            compA.device.flags.monitored.status,
            compB.device.flags.monitored.status,
          ),
        filterMultiple: false,
        filteredValue: nullifyIfEmpty(getFilterValues('isMonitored')),
        filters: [
          {
            text: (
              <span>
                <CheckCircleOutlined
                  style={{
                    color: 'green',
                    fontSize: '20px',
                    marginRight: '15px',
                  }}
                />
                &nbsp;Is Monitored
              </span>
            ),
            value: 'ACTIVE',
          },
          {
            text: (
              <span>
                <MinusCircleOutlined
                  style={{
                    fontSize: '20px',
                    marginRight: '15px',
                  }}
                />
                &nbsp;Not Monitored
              </span>
            ),
            value: 'INACTIVE',
          },
        ],
      });
    }

    if (canMultiSelect) {
      cols.splice(0, 0, {
        title: (
          <IBTooltip content={permissions.teamMember.tooltip}>
            <Badge
              title={'Selected Count'}
              count={isNotNull(selectedDevices) ? selectedDevices.length : 0}
              offset={[0, 2]}
            >
              <CloseCircleOutlined
                style={{ marginTop: 8, marginRight: 8 }}
                onClick={() => clearSelectedDevices()}
              />
            </Badge>
          </IBTooltip>
        ),
        render: (_, record: DeviceQueryResult) => (
          <Checkbox
            checked={
              isNull(selectedDevices)
                ? false
                : selectedDevices.findIndex(
                    (chk) => chk.device.id === record.device.id,
                  ) >= 0
            }
            onChange={(evt) => {
              if (evt.target.checked) {
                selectDevice(record);
              } else {
                deselectDevice(record);
              }
            }}
          />
        ),
      });
    }

    return cols;
  };

  const getDevices = () => {
    if (deviceData !== null) {
      if (deviceData.devices !== null) {
        return deviceData.devices;
      }
    }
    return [];
  };

  return (
    <IBTable
      showSorterTooltip
      columns={getColumns()}
      dataSource={getDevices()}
      loading={loading}
      onChange={(
        updatedPagination: TablePaginationConfig,
        filters: SorterResult<DeviceQueryResult>,
        sorter,
        extra,
      ) => handleTableChange(updatedPagination, filters, sorter, extra)}
      pagination={{
        current: input.pagination.page,
        pageSize: input.pagination.pageSize,
        total: totalRecords,
        showSizeChanger: true,
      }}
      rowKey={(record) => record.device.id}
    />
  );
};

export default DevicesContainerTable;
